/**
 * Best Practice User Context
 * Uses authStore as single source of truth
 * No localStorage for tokens (security best practice)
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { authStore } from '../services/authStore';
import { login as authLogin, logout as authLogout, initializeAuth, fetchUserInfo } from '../services/authService';

const UserContext = createContext(null);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [authState, setAuthState] = useState(() => authStore.getState());

  // Subscribe to authStore changes
  useEffect(() => {
    const unsubscribe = authStore.subscribe(() => {
      setAuthState(authStore.getState());
    });

    // Initialize auth on mount - only if not already initialized
    const currentState = authStore.getState();
    if (!currentState.isInitialized) {
      initializeAuth();
    }

    return unsubscribe;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Login handler
  const login = useCallback(async (email, password) => {
    try {
      await authLogin(email, password);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Login failed'
      };
    }
  }, []);

  // Logout handler
  const logout = useCallback(async () => {
    await authLogout();
  }, []);

  const contextValue = useMemo(() => ({
    user: authState.user || null,
    userType: authState.user?.userType || null,
    token: authState.accessToken,
    userId: authState.user?.id || null,
    email: authState.user?.email || null,
    loading: !authState.isInitialized,
    isAuthenticated: authState.isAuthenticated,
    isInitialized: authState.isInitialized,
    login,
    logout,
    refreshUser: fetchUserInfo,
  }), [authState, login, logout]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};